import { useTheme } from '@brunommsilva/react-foundations';
import CalculateIcon from '@mui/icons-material/Calculate';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DASHBOARD_PATH } from '../config/paths';
import { TEST_IDS } from '../config/test-ids';
import { useSession } from '../context/use-session';

const SignInPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, login } = useSession();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authenticating, setAuthenticating] = useState(false);

  const { darkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    if (isLoggedIn) {
      navigate(DASHBOARD_PATH);
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError(t('LOGIN_FAILED'));
      return;
    }

    try {
      setError(null);
      setAuthenticating(true);
      const success = await login(username.trim(), password.trim());
      if (success) {
        setUsername('');
        setPassword('');
        navigate(DASHBOARD_PATH);
      } else {
        setError(t('LOGIN_FAILED'));
      }
    } catch {
      setError(t('LOGIN_UNEXPECTED'));
    } finally {
      setAuthenticating(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #121212 0%, #1a1e2e 100%)'
            : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: 2,
        position: 'relative',
      }}
    >
      {/* Floating Theme Toggle */}
      <IconButton
        onClick={toggleDarkMode}
        color="inherit"
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 2,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
      <Container maxWidth="sm">
        {/* Brand Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '16px',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              mb: 2,
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 8px 16px rgba(0,0,0,0.4)'
                  : '0 8px 16px rgba(13, 148, 136, 0.24)',
            }}
          >
            <CalculateIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.5px',
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #2dd4bf 30%, #0d9488 90%)'
                  : 'linear-gradient(45deg, #0d9488 30%, #0f766e 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            {t('APP_NAME')}
          </Typography>
        </Box>

        {/* Card Form */}
        <Card
          elevation={0}
          sx={{
            p: { xs: 4, md: 5 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.08)',
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 24px 48px rgba(0,0,0,0.6)'
                : '0 20px 40px rgba(0,0,0,0.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                p: 1,
                borderRadius: '8px',
                display: 'flex',
                color: 'primary.contrastText',
              }}
            >
              <LockIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('SIGN_IN')}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              margin="normal"
              label={t('USERNAME_PLACEHOLDER')}
              variant="outlined"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError(null);
              }}
              placeholder={t('USERNAME_PLACEHOLDER')}
              disabled={authenticating}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
                  {
                    WebkitBoxShadow: (theme) =>
                      `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                    WebkitTextFillColor: (theme) =>
                      `${theme.palette.text.primary} !important`,
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
              }}
              autoFocus
              slotProps={{
                htmlInput: { 'data-testid': TEST_IDS.login.username },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label={t('PASSWORD_PLACEHOLDER')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              placeholder="••••••••"
              disabled={authenticating}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
                  {
                    WebkitBoxShadow: (theme) =>
                      `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                    WebkitTextFillColor: (theme) =>
                      `${theme.palette.text.primary} !important`,
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
              }}
              slotProps={{
                htmlInput: { 'data-testid': TEST_IDS.login.password },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 4, borderRadius: 2, fontWeight: 600 }}
              >
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              type="submit"
              data-testid={TEST_IDS.login.submit}
              variant="contained"
              color="primary"
              size="large"
              disabled={authenticating}
              sx={{
                py: 1.8,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '16px',
                textTransform: 'none',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 8px 20px rgba(0, 0, 0, 0.4)'
                    : '0 8px 20px rgba(13, 148, 136, 0.2)',
                '&:hover': {
                  boxShadow: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '0 12px 24px rgba(0, 0, 0, 0.6)'
                      : '0 12px 24px rgba(13, 148, 136, 0.3)',
                },
              }}
            >
              {authenticating ? t('LOGGING_IN') : t('SIGN_IN')}
            </Button>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default SignInPage;
